/**
 * Project: Gus's Auto Dealership
 * Author: Eihab Khudhair
 * Description: Footer component
 */

'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function Footer() {
  // Stable initial value so hydration never mismatches
  const [currentYear, setCurrentYear] = useState<number>(2025);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateYearAndScheduleNextMidnight = () => {
      const now = new Date();
      setCurrentYear(now.getFullYear());

      // Next midnight in the user's local timezone
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 0);

      const msUntilMidnight = nextMidnight.getTime() - now.getTime();

      timeoutId = setTimeout(() => {
        updateYearAndScheduleNextMidnight();
      }, msUntilMidnight);
    };

    // Run once after hydration, then schedule the exact midnight update
    updateYearAndScheduleNextMidnight();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <footer className="bg-gray-900 text-white mt-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4 text-blue-400">Gus&apos;s Auto Dealership</h3>
            <p className="text-gray-400">Your trusted source for quality vehicles.</p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link href="/" className="text-gray-400 hover:text-blue-400 transition">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/inventory" className="text-gray-400 hover:text-blue-400 transition">
                  Inventory
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-400 hover:text-blue-400 transition">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link href="/terms" className="text-gray-400 hover:text-blue-400 transition">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link href="/privacy" className="text-gray-400 hover:text-blue-400 transition">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>
            Designed by <span className="text-blue-400 font-semibold">Eihab Khudhair</span>
          </p>
          <p className="mt-2">© {currentYear} Gus&apos;s Auto Dealership. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
